import React, { useState } from 'react';
import { CheckIcon, XIcon } from 'lucide-react';
import { Car } from '../types/car';
import { formatARS, formatKm } from '../utils/format';
import { thumbUrl } from '../utils/image';
interface CompareTableProps {
  cars: Car[];
  onRemove: (id: string) => void;
  onFinance: (car: Car) => void;
  onInquiry: (car: Car) => void;
}
type Row = {
  label: string;
  value: (car: Car) => string;
  best?: 'min' | 'max';
  numeric?: (car: Car) => number;
};
type Section = {
  title: string;
  rows: Row[];
};
const sections: Section[] = [{
  title: 'Precio y financiación',
  rows: [{
    label: 'Precio contado',
    value: c => formatARS(c.cashPrice),
    best: 'min',
    numeric: c => c.cashPrice
  }, {
    label: 'Anticipo',
    value: c => formatARS(c.downPayment),
    best: 'min',
    numeric: c => c.downPayment
  }, {
    label: 'Cuotas aprox.',
    value: c => `${c.installments} x ${formatARS(c.installmentAmount)}`,
    best: 'min',
    numeric: c => c.installmentAmount
  }, {
    label: 'Total en cuotas',
    value: c => formatARS(c.installments * c.installmentAmount),
    best: 'min',
    numeric: c => c.installments * c.installmentAmount
  }]
}, {
  title: 'Ficha técnica',
  rows: [{
    label: 'Marca',
    value: c => c.brand
  }, {
    label: 'Modelo',
    value: c => c.model
  }, {
    label: 'Versión',
    value: c => c.version
  }, {
    label: 'Año',
    value: c => String(c.year),
    best: 'max',
    numeric: c => c.year
  }, {
    label: 'Kilometraje',
    value: c => formatKm(c.km),
    best: 'min',
    numeric: c => c.km
  }, {
    label: 'Condición',
    value: c => c.condition
  }, {
    label: 'Carrocería',
    value: c => c.bodyLabel
  }, {
    label: 'Color',
    value: c => c.color
  }, {
    label: 'Ubicación',
    value: c => c.location
  }, {
    label: 'Financiación',
    value: c => c.financing ? 'Sí' : 'Solo contado'
  }, {
    label: 'Acepta permuta',
    value: c => c.tradeIn ? 'Sí' : 'No'
  }, {
    label: 'Transmisión',
    value: c => c.transmission
  }, {
    label: 'Combustible',
    value: c => c.fuel
  }]
}];
export function CompareTable({
  cars,
  onRemove,
  onFinance,
  onInquiry
}: CompareTableProps) {
  const [onlyDiff, setOnlyDiff] = useState(false);
  if (cars.length === 0) {
    return <p className="py-10 text-center text-sm text-muted" data-id="element-159" data-mp="2rzl9j8ge1b0z">Elegí hasta 3 vehículos arriba para compararlos.</p>;
  }
  const bestValue = (row: Row): number | null => {
    if (!row.best || !row.numeric || cars.length < 2) return null;
    const values = cars.map(row.numeric);
    return row.best === 'min' ? Math.min(...values) : Math.max(...values);
  };
  const isVisible = (row: Row) => {
    if (!onlyDiff || cars.length < 2) return true;
    return new Set(cars.map(row.value)).size > 1;
  };
  const colSpan = cars.length + 1;
  return <div data-id="element-160" data-mp="1zbphwi5oe9b2">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3" data-id="element-161" data-mp="1zaxmp8fzqdlb">
        <p className="text-xs text-muted" data-id="element-162" data-mp="24aiu87987u9n">
          {cars.length < 2 ? 'Sumá al menos un vehículo más para comparar.' : 'En azul, el mejor valor de cada fila.'}
        </p>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-white" data-id="element-163" data-mp="1kqtfp505d400">
          <input type="checkbox" checked={onlyDiff} onChange={e => setOnlyDiff(e.target.checked)} className="h-4 w-4 accent-neon" data-id="element-164" data-mp="1goq66lx1g9hd" />
          Mostrar solo diferencias
        </label>
      </div>

      <div className="-mx-6 overflow-x-auto px-6" data-id="element-165" data-mp="23n316dddowu1">
        <table className="w-full min-w-[680px] table-fixed border-collapse text-sm" data-id="element-166" data-mp="23ls22quvod99">
          <thead data-id="element-167" data-mp="1zlhsz5bsh6q6">
            <tr data-id="element-168" data-mp="1zjwy1elkblrs">
              <th scope="col" className="w-36 pb-4" data-id="element-169" data-mp="2ddehz0i5v0ic">
                <span className="sr-only" data-id="element-170" data-mp="2dc36eeih4ogo">Característica</span>
              </th>
              {cars.map(car => <th key={car.id} scope="col" className="px-3 pb-4 text-left align-top font-normal" data-id="element-171" data-mp="28f36klc7why0">
                  <div className="relative" data-id="element-172" data-mp="2nbu2gkmonbiv">
                    <img src={thumbUrl(car.image)} alt="" className="aspect-[3/2] w-full rounded-lg bg-ink object-cover" data-id="element-173" data-mp="2id0u16m96apk" />
                    <button type="button" onClick={() => onRemove(car.id)} aria-label={`Quitar ${car.model} de la comparación`} className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-md border border-line bg-ink/85 text-white transition-colors duration-150 hover:text-neon focus:outline-none focus-visible:ring-2 focus-visible:ring-neon" data-id="element-174" data-mp="fp2bh5l03hc1">
                      <XIcon className="h-3.5 w-3.5" data-id="element-175" data-mp="1pj6umreeo4h" />
                    </button>
                  </div>
                  <p className="mt-3 text-xs text-muted" data-id="element-176" data-mp="6ovzxp8v4rfi">{car.brand}</p>
                  <p className="font-display text-lg font-semibold leading-tight text-white" data-id="element-177" data-mp="bo8kg3c9hmex">
                    {car.model} <span className="font-medium text-muted" data-id="element-178" data-mp="1i4kmwgm49qgb">{car.version}</span>
                  </p>
                  <p className="mt-1 font-display text-xl font-bold tabular-nums text-neon" data-id="element-179" data-mp="1n35ho0ilnhxd">{formatARS(car.cashPrice)}</p>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-180" data-mp="32mlfokinn7bs">
            {sections.map(section => {
            const visibleRows = section.rows.filter(isVisible);
            if (visibleRows.length === 0) return null;
            return <React.Fragment key={section.title} data-id="element-181" data-mp="2sx1vnbj7h7fc">
                  <tr data-id="element-182" data-mp="2swa1m559bhr6">
                    <th colSpan={colSpan} scope="colgroup" className="pb-2 pt-6 text-left font-display text-base font-semibold uppercase tracking-wide text-neon" data-id="element-183" data-mp="2nxgwb004ix23">
                      {section.title}
                    </th>
                  </tr>
                  {visibleRows.map(row => {
                const best = bestValue(row);
                return <tr key={row.label} className="border-t border-line" data-id="element-184" data-mp="29zin090ntqtg">
                        <th scope="row" className="py-3 pr-3 text-left text-xs font-medium text-muted" data-id="element-185" data-mp="d430dvtimzct">
                          {row.label}
                        </th>
                        {cars.map(car => {
                    const isBest = best !== null && row.numeric?.(car) === best;
                    return <td key={car.id} className={`px-3 py-3 tabular-nums ${isBest ? 'font-semibold text-neon' : 'text-white'}`} data-id="element-186" data-mp="1sodbmtmy5huw">
                              {row.value(car)}
                            </td>;
                  })}
                      </tr>;
              })}
                </React.Fragment>;
          })}

            <tr data-id="element-187" data-mp="2q9rejgz5favz">
              <th colSpan={colSpan} scope="colgroup" className="pb-2 pt-6 text-left font-display text-base font-semibold uppercase tracking-wide text-neon" data-id="element-188" data-mp="2bf3c6bp1oskl">
                Detalles
              </th>
            </tr>
            {!onlyDiff && <tr className="border-t border-line" data-id="element-189" data-mp="zy4saygocreq">
                <th scope="row" className="py-3 pr-3 text-left align-top text-xs font-medium text-muted" data-id="element-190" data-mp="14zak8u2igan7">
                  Descripción
                </th>
                {cars.map(car => <td key={car.id} className="px-3 py-3 align-top leading-6 text-white/80" data-id="element-191" data-mp="3e7pcrrtkvyo8">
                    {car.description}
                  </td>)}
              </tr>}
            {cars.some(car => car.features.length > 0) && <tr className="border-t border-line" data-id="element-192" data-mp="3o3imz4jtrg2u">
                <th scope="row" className="py-3 pr-3 text-left align-top text-xs font-medium text-muted" data-id="element-193" data-mp="30f94ocltlagg">
                  Equipamiento
                </th>
                {cars.map(car => <td key={car.id} className="px-3 py-3 align-top" data-id="element-194" data-mp="398m6k41v3meo">
                    {car.features.length > 0 ? <ul className="space-y-1.5" data-id="element-195" data-mp="34guhxz29nqr6">
                        {car.features.map(f => <li key={f} className="flex items-start gap-2 text-white" data-id="element-196" data-mp="2gqiaepaxnw14">
                            <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-neon" aria-hidden="true" data-id="element-197" data-mp="2cponnz988n9t" />
                            {f}
                          </li>)}
                      </ul> : <span className="text-muted" data-id="element-198" data-mp="36m6l6vkqqbob">—</span>}
                  </td>)}
              </tr>}

            <tr className="border-t border-line" data-id="element-199" data-mp="3fltdmrvhs758">
              <th scope="row" className="pt-4" data-id="element-200" data-mp="3kk4o94tm2loi">
                <span className="sr-only" data-id="element-201" data-mp="3kjboint2kwfv">Acciones</span>
              </th>
              {cars.map(car => <td key={car.id} className="space-y-2 px-3 pt-4" data-id="element-202" data-mp="3phn0b42xdh0q">
                  <button type="button" onClick={() => onFinance(car)} className="h-10 w-full rounded-lg bg-neon text-sm font-semibold text-ink transition-colors duration-150 hover:bg-neon-bright focus:outline-none focus-visible:ring-2 focus-visible:ring-neon" data-id="element-203" data-mp="3u9o5y75l89hs">
                    Financiar
                  </button>
                  <button type="button" onClick={() => onInquiry(car)} className="h-10 w-full rounded-lg border border-line text-sm font-medium text-white transition-colors duration-150 hover:border-neon/60 hover:text-neon focus:outline-none focus-visible:ring-2 focus-visible:ring-neon" data-id="element-204" data-mp="m8i5ehxyhog4">
                    Consultar
                  </button>
                </td>)}
            </tr>
          </tbody>
        </table>
      </div>
    </div>;
}